import { Locale } from '@/lib/i18n/dictionaries';

import { env } from './process';

export { env };

export const isProdNodeEnv = env.nodeEnv === 'production';
export const isLocalNodeEnv = env.nodeEnv === 'development';

export const isProd = env.publicEnv === 'prod';
export const isStage = env.publicEnv === 'stage';
export const isDev = env.publicEnv === 'dev';

export const isTestEnv = env.isTestEnv;

export const isNonProd = isDev || isStage;

/**
 * In-cluster nginx sidecar. Used for server→server calls in prod/stage (except zh-cn).
 * Isolated deployments override it via INTERNAL_SELF_URL.
 */
export const nginxAddress =
  env.internalSelfUrl || `http://decodo-website-nginx:8088`;

export const isGitlabCI =
  env.ci === 'true' ||
  env.githubActions === 'true' ||
  env.gitlabCi === 'true' ||
  env.bitbucketBuildNumber !== undefined;

export const envDomain = env.publicDomain || 'https://decodo.com';

export const isBuildPhase = env.nextPhase === 'phase-production-build';

type EnvKey = 'dev' | 'prod' | 'stage';
type PhaseKey = 'build' | 'runtime-server' | 'runtime-client';

export const cmsEndpointMap: Record<
  EnvKey,
  Record<PhaseKey, string | undefined>
> = {
  dev: {
    build: env.publicStrapiApiUrl,
    'runtime-server': env.publicStrapiApiUrl,
    'runtime-client': env.publicStrapiApiUrl,
  },
  prod: {
    build: env.strapiApiUrl,
    'runtime-server': env.internalStrapiApiUrl,
    'runtime-client': env.publicStrapiApiUrl,
  },
  stage: {
    build: env.strapiApiUrl,
    'runtime-server': env.internalStrapiApiUrl,
    'runtime-client': env.publicStrapiApiUrl,
  },
};

const currentEnv: EnvKey = (() => {
  if (isDev) return 'dev';
  if (isStage) return 'stage';

  return 'prod';
})();

const currentPhase: PhaseKey = (() => {
  if (isBuildPhase) return 'build';
  if (typeof window !== 'undefined') return 'runtime-client';

  return 'runtime-server';
})();

export const cmsApiEndpoint = cmsEndpointMap[currentEnv][currentPhase];

export const isPreviewPage = (() => {
  if (typeof window === 'undefined') return false;

  return window.location.pathname.includes('/preview-n');
})();

const defaultSelfEndpoints: Record<
  EnvKey,
  Record<PhaseKey, string | undefined>
> = {
  dev: {
    build: 'http://localhost:3000',
    'runtime-server': 'http://localhost:3000',
    'runtime-client': 'http://localhost:3000',
  },
  prod: {
    build: nginxAddress,
    'runtime-server': nginxAddress,
    'runtime-client': env.publicDomain,
  },
  stage: {
    build: nginxAddress,
    'runtime-server': nginxAddress,
    'runtime-client': env.publicDomain,
  },
};

const zhCnSelfEndpoints: Record<
  EnvKey,
  Record<PhaseKey, string | undefined>
> = {
  ...defaultSelfEndpoints,
  prod: {
    ...defaultSelfEndpoints.prod,
    build: env.publicDomain,
    'runtime-server': env.publicDomain,
  },
  stage: {
    ...defaultSelfEndpoints.stage,
    build: env.publicDomain,
    'runtime-server': env.publicDomain,
  },
};

const selfEndpointMap: Record<
  Locale,
  Record<EnvKey, Record<PhaseKey, string | undefined>>
> = {
  en: defaultSelfEndpoints,
  'zh-cn': zhCnSelfEndpoints,
};

export const selfEndpoint =
  selfEndpointMap[env.publicLocale][currentEnv][currentPhase]!;

export const nextApiEndpoint = `${selfEndpoint}/next-api`;
